/**
 * QMS API Client
 * Handles all API calls for the Quality Management System
 */

import { api } from '@/lib/api';
import type {
  QmsBoardResponse,
  QmsTicket,
  QmsTicketPayload,
  QmsMoveTicketPayload,
  QmsQdApprovalPayload,
  QmsCeoReviewPayload,
  QmsCreateCapaPayload,
  QmsComment,
  QmsTimelineEvent,
  QmsTrendAnalysis,
  QmsTrendHistory,
  QmsTrendPatterns,
  QmsTrendSummaryResponse,
  QmsPatternSeverity,
  QmsPatternType,
  QmsAttachment,
  QmsLineItem,
  QmsGateway,
  QmsShipping,
  QmsSiteVisit,
  QmsWorkflowActionPayload,
} from '../types/qms.types';

/**
 * Unwrap API response that may be nested under a key or returned flat
 * Handles both { ticket: {...} } and {...} response formats
 */
function unwrapResponse<T>(response: Record<string, unknown>, key: string): T {
  if (key in response && response[key] != null) {
    return response[key] as T;
  }
  return response as T;
}

/**
 * Fetch the entire Kanban board with all columns
 */
export async function fetchQmsBoard(
  organisation: string,
  options: { type?: QmsTicket['type']; accountId?: string } = {}
): Promise<QmsBoardResponse> {
  const params: Record<string, string> = { organisation };
  if (options.type) params.type = options.type;
  if (options.accountId && options.accountId !== 'all') params.account_id = options.accountId;
  return api.get('/v4/qmstickets/board', params);
}

/**
 * Fetch a single ticket with populated fields
 */
export async function fetchQmsTicket(
  ticketId: string,
  populate?: string[]
): Promise<QmsTicket> {
  const params: Record<string, string> = {};

  if (populate && populate.length > 0) {
    params.populate = JSON.stringify(populate);
  }

  const response = await api.get<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}`,
    params
  );

  // viewerIsFollower is returned as a sibling of `ticket` (not inside it) because
  // it's a per-viewer computed value, not a stored ticket field.
  const ticket = unwrapResponse<QmsTicket>(response, 'ticket');
  return { ...ticket, viewerIsFollower: response.viewerIsFollower as boolean | undefined };
}

/**
 * Create a new QMS ticket
 */
export async function createQmsTicket(payload: QmsTicketPayload): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>('/v4/qmstickets', payload);
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * Update an existing QMS ticket
 */
export async function updateQmsTicket(
  ticketId: string,
  payload: Partial<QmsTicketPayload>
): Promise<QmsTicket> {
  const response = await api.put<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}`,
    payload
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * Move a ticket to a different column (change status)
 */
export async function moveQmsTicket(
  ticketId: string,
  payload: QmsMoveTicketPayload
): Promise<QmsTicket> {
  const response = await api.put<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/move`,
    payload
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

export async function applyQmsWorkflowAction(
  ticketId: string,
  payload: QmsWorkflowActionPayload,
): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/workflow-action`,
    payload,
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * QD Approval action (approve, reject, request changes)
 */
export async function qdApproveTicket(
  ticketId: string,
  payload: QmsQdApprovalPayload
): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/approve`,
    payload
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * CEO Review action (confirm, adjust priority)
 */
export async function ceoReviewTicket(
  ticketId: string,
  payload: QmsCeoReviewPayload
): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/ceo-review`,
    payload
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * Fetch comments/notes for a ticket
 * Note: Comments are embedded in the ticket document, so this fetches the ticket
 * and returns just the notes array (excluding deleted notes)
 */
export async function fetchQmsComments(ticketId: string): Promise<QmsComment[]> {
  const ticket = await fetchQmsTicket(ticketId, ['user_name']);
  // Filter out deleted notes and return only visible ones
  return (ticket.notes || []).filter((note) => !note.deleted);
}

/**
 * Add a comment/note to a ticket
 */
export async function addQmsComment(
  ticketId: string,
  content: string
): Promise<QmsComment> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/notes`,
    { content }
  );
  return unwrapResponse<QmsComment>(response, 'note');
}

/**
 * Edit a comment/note
 */
export async function editQmsComment(
  ticketId: string,
  noteId: string,
  content: string
): Promise<void> {
  await api.put(`/v4/qmstickets/${ticketId}/notes/${noteId}`, { content });
}

/**
 * Delete a comment/note (soft delete)
 */
export async function deleteQmsComment(ticketId: string, noteId: string): Promise<void> {
  await api.delete(`/v4/qmstickets/${ticketId}/notes/${noteId}`);
}

/**
 * Fetch timeline/audit trail for a ticket
 */
export async function fetchQmsTimeline(ticketId: string): Promise<QmsTimelineEvent[]> {
  const response = await api.get<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/timeline`
  );
  return unwrapResponse<QmsTimelineEvent[]>(response, 'timeline');
}

/**
 * Delete a QMS ticket
 */
export async function deleteQmsTicket(ticketId: string): Promise<void> {
  await api.delete(`/v4/qmstickets/${ticketId}`);
}

/**
 * Search for linkable tickets
 */
export async function searchLinkableTickets(
  organisation: string,
  searchTerm: string,
  excludeTicketId?: string,
  excludeLinkedIds?: string[]
): Promise<QmsTicket[]> {
  const params: Record<string, string> = {
    organisation,
    search: searchTerm,
  };

  if (excludeTicketId) {
    params.exclude = excludeTicketId;
  }

  if (excludeLinkedIds && excludeLinkedIds.length > 0) {
    params.excludeLinked = excludeLinkedIds.join(',');
  }

  const response = await api.get<Record<string, unknown>>(
    '/v4/qmstickets/search/linkable',
    params
  );
  return unwrapResponse<QmsTicket[]>(response, 'tickets');
}

/**
 * Link a ticket to another ticket with a relationship type
 */
export async function linkQmsTicket(
  ticketId: string,
  relatedTicketId: string,
  relationship: string
): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/link`,
    { related_ticket_id: relatedTicketId, relationship }
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * Unlink a ticket from another ticket
 */
export async function unlinkQmsTicket(
  ticketId: string,
  relatedTicketId: string
): Promise<QmsTicket> {
  const response = await api.delete<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/link/${relatedTicketId}`
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * Create a CAPA from an existing ticket
 */
export async function createCapaFromTicket(
  ticketId: string,
  payload: QmsCreateCapaPayload
): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/create-capa`,
    payload
  );
  // Try 'capa' key first, then fall back to 'ticket'
  if ('capa' in response && response.capa != null) {
    return response.capa as QmsTicket;
  }
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * Escalate a QD-approved ticket to CEO review
 */
export async function escalateToCeo(ticketId: string): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/escalate-ceo`,
    {}
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * Submit (or resubmit) a ticket for QD approval
 */
export async function submitForApproval(ticketId: string): Promise<QmsTicket> {
  const response = await api.post<Record<string, unknown>>(
    `/v4/qmstickets/${ticketId}/submit-for-approval`,
    {}
  );
  return unwrapResponse<QmsTicket>(response, 'ticket');
}

/**
 * QMS Trend Analysis API Functions
 */

/**
 * Fetch latest trend analysis for an organisation
 */
export async function fetchQmsTrends(organisation: string): Promise<QmsTrendAnalysis> {
  return api.get<QmsTrendAnalysis>('/v4/qms-trends', { organisation });
}

/**
 * Fetch historical trend data (time series)
 */
export async function fetchQmsTrendHistory(
  organisation: string,
  days: number = 30
): Promise<QmsTrendHistory> {
  return api.get<QmsTrendHistory>('/v4/qms-trends/history', {
    organisation,
    days: days.toString(),
  });
}

/**
 * Fetch patterns with optional filtering
 */
export async function fetchQmsTrendPatterns(
  organisation: string,
  options?: {
    severity?: QmsPatternSeverity[];
    pattern_type?: QmsPatternType[];
  }
): Promise<QmsTrendPatterns> {
  const params: Record<string, string> = { organisation };

  if (options?.severity && options.severity.length > 0) {
    params.severity = options.severity.join(',');
  }

  if (options?.pattern_type && options.pattern_type.length > 0) {
    params.pattern_type = options.pattern_type.join(',');
  }

  return api.get<QmsTrendPatterns>('/v4/qms-trends/patterns', params);
}

/**
 * Fetch aggregated trend summary statistics
 */
export async function fetchQmsTrendSummary(
  organisation: string,
  days: number = 30
): Promise<QmsTrendSummaryResponse> {
  return api.get<QmsTrendSummaryResponse>('/v4/qms-trends/summary', {
    organisation,
    days: days.toString(),
  });
}

// =============================================================================
// Attachments
// =============================================================================

interface V3AttachmentsResponse {
  attachments: QmsAttachment[];
}

/**
 * Fetch attachments for a ticket
 */
export async function fetchQmsAttachments(ticketId: string): Promise<QmsAttachment[]> {
  const response = await api.get<V3AttachmentsResponse>(
    `/v4/qmstickets/${ticketId}/attachments`
  );
  return response.attachments || [];
}

/**
 * Upload a file attachment to a ticket
 */
export async function uploadQmsAttachment(ticketId: string, file: File): Promise<QmsAttachment> {
  const formData = new FormData();
  formData.append('file', file);

  return api.upload<QmsAttachment>(
    `/v4/qmstickets/${ticketId}/attachments`,
    formData
  );
}

/**
 * Delete a ticket attachment
 */
export async function deleteQmsAttachment(
  ticketId: string,
  attachmentId: string
): Promise<{ success: boolean }> {
  return api.delete(`/v4/qmstickets/${ticketId}/attachments/${attachmentId}`);
}

/**
 * Get the download URL for an attachment
 */
export function getQmsAttachmentDownloadUrl(ticketId: string, attachmentId: string): string {
  return `/api/v4/qmstickets/${ticketId}/attachments/${attachmentId}/download`;
}

// =============================================================================
// Users
// =============================================================================

interface QmsUsersResponse {
  users: Array<{
    _id: string;
    email: string;
    firstname: string;
    lastname: string;
    username?: string;
  }>;
}

/**
 * Fetch ATEK internal staff (employees + contractors) for QMS ticket
 * assignment. V4 (PostgreSQL) only — no Mongo organisation scope.
 *
 * @param _organisationId - Deprecated. Kept for callsite stability; ignored.
 * @param search - Optional search term to filter by name/email.
 */
export async function fetchQmsUsers(
  _organisationId: string,
  search?: string
): Promise<QmsUsersResponse> {
  const params: Record<string, string | number> = {
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
    pageLength: 50,
  };
  if (search && search.trim().length > 0) {
    params.search = search.trim();
  }

  const response = await api.get<{
    users: Array<{
      pg_id: string;
      email: string;
      firstname?: string;
      lastname?: string;
      username?: string;
    }>;
    total: number;
  }>('/v4/users', params);

  return {
    users: (response.users || [])
      .filter((u) => !EXCLUDED_EMAILS.includes(u.email.toLowerCase()))
      .map((u) => ({
        _id: u.pg_id,
        email: u.email,
        firstname: u.firstname || '',
        lastname: u.lastname || '',
        username: u.username,
      })),
  };
}

interface V3UserResponse {
  user: {
    _id: string;
    email: string;
    firstname?: string;
    lastname?: string;
    username?: string;
  };
}

export interface QmsUserDetails {
  _id: string;
  email: string;
  firstname: string;
  lastname: string;
  username?: string;
}

/**
 * Fetch a single user by ID
 * Used to display selected user when only ID is available
 */
export async function fetchQmsUserById(userId: string): Promise<QmsUserDetails | null> {
  try {
    const response = await api.get<V3UserResponse>(`/v4/legacy/users/${userId}`);
    const u = response.user;
    if (!u) return null;
    return {
      _id: u._id,
      email: u.email,
      firstname: u.firstname || '',
      lastname: u.lastname || '',
      username: u.username,
    };
  } catch {
    // User not found or error - return null
    return null;
  }
}

// =============================================================================
// Followers
// =============================================================================

/**
 * Add a follower to a QMS ticket.
 * Returns the updated followers array (Mongo user IDs).
 */
export async function addQmsFollower(ticketId: string, userId: string): Promise<string[]> {
  const response = await api.post<{ followers: string[] }>(
    `/v4/qmstickets/${ticketId}/followers`,
    { userId }
  );
  return response.followers;
}

/**
 * Remove a follower from a QMS ticket.
 * Returns the updated followers array (Mongo user IDs).
 */
export async function removeQmsFollower(ticketId: string, userId: string): Promise<string[]> {
  const response = await api.delete<{ followers: string[] }>(
    `/v4/qmstickets/${ticketId}/followers/${userId}`
  );
  return response.followers;
}

export interface FollowerSelfResponse {
  followers: string[];
  viewerIsFollower: boolean;
}

/**
 * Follow this ticket as the currently authenticated user.
 * Backend resolves the viewer identity from the session (no userId param).
 */
export async function followSelf(ticketId: string): Promise<FollowerSelfResponse> {
  return api.post<FollowerSelfResponse>(
    `/v4/qmstickets/${ticketId}/followers/me`,
    {}
  );
}

/**
 * Unfollow this ticket as the currently authenticated user.
 * Backend resolves the viewer identity from the session (no userId param).
 */
export async function unfollowSelf(ticketId: string): Promise<FollowerSelfResponse> {
  return api.delete<FollowerSelfResponse>(
    `/v4/qmstickets/${ticketId}/followers/me`
  );
}

// =============================================================================
// Permission Management — V4 (PostgreSQL) only
// =============================================================================

export interface QmsUserWithPermissions {
  /** PG UUID. */
  _id: string;
  email: string;
  firstname: string;
  lastname: string;
  permission_strings: string[];
}

interface V4UsersResponse {
  users: Array<{
    pg_id: string;
    email: string;
    firstname?: string;
    lastname?: string;
    permission_strings?: string[];
  }>;
  total: number;
}

const EXCLUDED_EMAILS = [
  'a.sicimli@lechiffre.ca',
  'claudecode@atek.io',
  'omar@test.io',
  'appscripts@atek.io',
  'cindyatek@yopmail.com',
];

/**
 * Fetch ATEK employees + contractors with their permission_strings.
 * "Internal staff" = `employment_type ∈ {employee, contractor}`. No org scope.
 */
export async function fetchUsersWithPermissions(
  search?: string
): Promise<QmsUserWithPermissions[]> {
  const params: Record<string, string | number> = {
    employment_type: 'employee,contractor',
    enabled: 'true',
    is_subaccount: 'false',
    pageLength: 100,
  };
  if (search) params.search = search;

  const response = await api.get<V4UsersResponse>('/v4/users', params);

  return (response.users || [])
    .filter((u) => !EXCLUDED_EMAILS.includes(u.email.toLowerCase()))
    .map((u) => ({
      _id: u.pg_id,
      email: u.email,
      firstname: u.firstname || '',
      lastname: u.lastname || '',
      permission_strings: u.permission_strings || [],
    }));
}

/**
 * Update a user's permission_strings. `userId` must be the PG UUID.
 */
export async function updateUserPermissions(
  userId: string,
  permissionStrings: string[]
): Promise<void> {
  await api.put(`/v4/users/${userId}/permission_strings`, {
    permission_strings: permissionStrings,
  });
}

// =============================================================================
// AI Investigation
// =============================================================================

import type { QmsAiInvestigation, QmsTicketWithAi } from '../types/qms.types';

interface AiInvestigationResponse {
  investigation: QmsAiInvestigation | null;
}

/**
 * Fetch AI investigation for a ticket
 */
export async function fetchQmsAiInvestigation(
  ticketId: string
): Promise<QmsAiInvestigation | null> {
  const response = await api.get<AiInvestigationResponse>(
    `/v4/qmstickets/${ticketId}/ai-investigation`
  );
  return response.investigation;
}

/**
 * Retry a failed AI investigation
 */
export async function retryQmsAiInvestigation(
  ticketId: string
): Promise<{ success: boolean; investigationId: string }> {
  return api.post(`/v4/qmstickets/${ticketId}/ai-investigation/retry`, {});
}

/**
 * Trigger AI investigation for a ticket that doesn't have one
 */
export async function triggerQmsAiInvestigation(
  ticketId: string,
  rerun = false
): Promise<{ success: boolean; investigationId: string }> {
  return api.post(`/v4/qmstickets/${ticketId}/ai-investigation/trigger`, rerun ? { rerun: true } : {});
}

/**
 * Accept AI findings and apply them to the ticket
 * @param ticketId - Ticket ID
 * @param fields - Fields to accept: ['all'] or specific fields like ['summary', 'rootCauses', 'recommendations', 'riskLevel', 'priority', 'tags']
 */
export async function acceptQmsAiFindings(
  ticketId: string,
  fields: string[] = ['all']
): Promise<QmsTicketWithAi> {
  const response = await api.post<{ ticket: QmsTicketWithAi }>(
    `/v4/qmstickets/${ticketId}/ai-investigation/accept`,
    { fields }
  );
  return response.ticket;
}

// ==================== Client Ticket Sub-Resource API ====================

// --- Line Items ---

export async function addLineItem(
  ticketId: string,
  data: Partial<QmsLineItem>
): Promise<QmsLineItem> {
  const response = await api.post<{ line_item: QmsLineItem }>(
    `/v4/qmstickets/${ticketId}/line-items`,
    data
  );
  return response.line_item;
}

export async function updateLineItem(
  ticketId: string,
  itemId: string,
  data: Partial<QmsLineItem>
): Promise<QmsTicket> {
  const response = await api.put<{ ticket: QmsTicket }>(
    `/v4/qmstickets/${ticketId}/line-items/${itemId}`,
    data
  );
  return response.ticket;
}

export async function deleteLineItem(
  ticketId: string,
  itemId: string
): Promise<QmsTicket> {
  const response = await api.delete<{ ticket: QmsTicket }>(
    `/v4/qmstickets/${ticketId}/line-items/${itemId}`
  );
  return response.ticket;
}

// --- Gateways ---

export async function addGateway(
  ticketId: string,
  data: Partial<QmsGateway>
): Promise<QmsGateway> {
  const response = await api.post<{ gateway: QmsGateway }>(
    `/v4/qmstickets/${ticketId}/gateways`,
    data
  );
  return response.gateway;
}

export async function updateGateway(
  ticketId: string,
  gatewayId: string,
  data: Partial<QmsGateway>
): Promise<QmsTicket> {
  const response = await api.put<{ ticket: QmsTicket }>(
    `/v4/qmstickets/${ticketId}/gateways/${gatewayId}`,
    data
  );
  return response.ticket;
}

export async function deleteGateway(
  ticketId: string,
  gatewayId: string
): Promise<QmsTicket> {
  const response = await api.delete<{ ticket: QmsTicket }>(
    `/v4/qmstickets/${ticketId}/gateways/${gatewayId}`
  );
  return response.ticket;
}

// --- Shipping ---

export async function upsertShipping(
  ticketId: string,
  data: Partial<QmsShipping>
): Promise<QmsShipping> {
  const response = await api.post<{ shipping: QmsShipping }>(
    `/v4/qmstickets/${ticketId}/shipping`,
    data
  );
  return response.shipping;
}

export async function deleteShipping(
  ticketId: string,
  shippingId: string
): Promise<QmsTicket> {
  const response = await api.delete<{ ticket: QmsTicket }>(
    `/v4/qmstickets/${ticketId}/shipping/${shippingId}`
  );
  return response.ticket;
}

// --- Site Visits ---

export async function addSiteVisit(
  ticketId: string,
  data: Partial<QmsSiteVisit>
): Promise<QmsSiteVisit> {
  const response = await api.post<{ site_visit: QmsSiteVisit }>(
    `/v4/qmstickets/${ticketId}/site-visits`,
    data
  );
  return response.site_visit;
}

export async function updateSiteVisit(
  ticketId: string,
  visitId: string,
  data: Partial<QmsSiteVisit>
): Promise<QmsTicket> {
  const response = await api.put<{ ticket: QmsTicket }>(
    `/v4/qmstickets/${ticketId}/site-visits/${visitId}`,
    data
  );
  return response.ticket;
}

export async function deleteSiteVisit(
  ticketId: string,
  visitId: string
): Promise<QmsTicket> {
  const response = await api.delete<{ ticket: QmsTicket }>(
    `/v4/qmstickets/${ticketId}/site-visits/${visitId}`
  );
  return response.ticket;
}
